import { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';
import { Heading, Link, Paragraph } from '@digdir/designsystemet-react';
import type { HrOverviewTeam } from '../../../../api/hrWorkEnvironment';
import {
  leadersText,
  nextText,
  processPhases,
  teamRoute,
} from '../Overview/hrOverviewView';
import { useUsesSafetyReps } from '../../../../hooks/queries/hms/useHmsSettingsQuery';
import { safetyRepText } from '../../safetyRepText';
import { OverviewSubpage } from '../Overview/OverviewSubpage';
import classes from '../Overview/Overview.module.css';

const TeamName = ({ team }: { team: HrOverviewTeam }): ReactElement => {
  const route = teamRoute(team);
  return route ? (
    <Link asChild data-size="sm">
      <RouterLink to={route}>{team.name}</RouterLink>
    </Link>
  ) : (
    <span className="ds-paragraph" data-size="sm">
      {team.name}
    </span>
  );
};

const Process = ({ teams }: { teams: HrOverviewTeam[] }): ReactElement => {
  const { t, i18n } = useTranslation();
  const text = safetyRepText(useUsesSafetyReps());
  return (
    <ol className={classes.phases}>
      {processPhases(teams).map(({ phase, count, steps }) => (
        <li key={phase} className={classes.card}>
          <div className={classes.phaseHeading}>
            <div className={classes.heading}>
              <Heading level={2} data-size="xs">
                {t(`hrOverview.phases.${phase}.title`)}
              </Heading>
              <Paragraph data-size="sm" className={classes.subtle}>
                {t(text(`hrOverview.phases.${phase}.intro`))}
              </Paragraph>
            </div>
            <span className="ds-label" data-size="sm">
              {t('hrOverview.teamCount', { count })}
            </span>
          </div>
          {steps.map(({ index, step, teams: inStep }) => (
            <div key={step} className={classes.step}>
              <Heading level={3} data-size="2xs">
                {t('hrOverview.process.step', {
                  number: index + 1,
                  step: t(`workEnvironmentProcess.steps.${step}.title`),
                })}{' '}
                <span className={classes.stepCount}>
                  {t('hrOverview.process.count', { count: inStep.length })}
                </span>
              </Heading>
              {inStep.length === 0 ? (
                <Paragraph data-size="sm" className={classes.subtle}>
                  {t('hrOverview.process.none')}
                </Paragraph>
              ) : (
                <ul className={classes.stepTeams}>
                  {inStep.map((team) => (
                    <li key={team.id}>
                      <TeamName team={team} />
                      <span
                        className={`ds-paragraph ${classes.subtle}`}
                        data-size="sm"
                      >
                        {leadersText(team, t)}
                      </span>
                      <span className="ds-paragraph" data-size="sm">
                        {nextText(team, t, i18n.language)}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ))}
        </li>
      ))}
    </ol>
  );
};

export const HRProcess = (): ReactElement => (
  <OverviewSubpage page="process">
    {(overview) => <Process teams={overview.teams} />}
  </OverviewSubpage>
);
